import FullPageLoading from "components/FullPageLoading"
import posthog from "posthog-js"
import { useEffect as useEffect, useState } from "react"
import { Outlet, useLocation, useSearchParams } from "react-router-dom"
import {
  BROWSER_STORAGE_KEYS,
  importantSearchParams,
  Platforms,
} from "utils/constants"
import { detectPlatformFromPath } from "utils/platform"
import { readShadowFromSearchParams, SHADOW_SEARCH_PARAM } from "utils/shadow"
import { trackingService } from "utils/tracking"
import { GA } from "utils/tracking/ga"

import { AuthManager } from "./AuthManager"

const getIpAddress = async (): Promise<string | null> => {
  try {
    const res = await fetch("https://api.ipify.org?format=json")
    const data = await res.json()
    return data.ip
  } catch (e) {
    console.warn("Failed to fetch IP address", e)
    return null
  }
}

const StudentRoleAuth = ({
  setProcessingAuth,
}: {
  setProcessingAuth: (value: boolean) => void
}) => {
  const platform = Platforms.student
  const [searchParams, setSearchParams] = useSearchParams()
  const code = searchParams.get("code")
  const shadow = readShadowFromSearchParams(searchParams)
  const location = useLocation()

  const auth = AuthManager.getPlatformAuth(platform)

  useEffect(() => {
    GA.init()
  }, [])

  useEffect(() => {
    posthog.capture("$pageview")
  }, [location])

  useEffect(() => {
    const run = async () => {
      try {
        const ipAddress = await getIpAddress()
        if (ipAddress) {
          localStorage.setItem(BROWSER_STORAGE_KEYS.ipAddress, ipAddress)
        }
      } catch (e) {
        console.warn("Failed to fetch IP address", e)
      }
    }

    run()
  }, [])

  useEffect(() => {
    const run = async () => {
      try {
        // If "code" exists → process auth code
        let invalidAuthCode = false
        if (code) {
          const newAuth = await AuthManager.processAuthCode(
            code,
            platform,
            shadow
          )
          if (!newAuth) invalidAuthCode = true
          if (newAuth) {
            trackingService.initUser(newAuth)

            searchParams.delete("code")
            searchParams.delete("platform")
            searchParams.delete(SHADOW_SEARCH_PARAM)

            setSearchParams(searchParams, { replace: true })
            setProcessingAuth(false)
            return
          }
        }

        // If stored auth exists → verify auth is valid or not
        if (auth) {
          const verified = await AuthManager.verifyToken(platform)
          if (verified) {
            trackingService.initUser(auth)

            setSearchParams(searchParams, { replace: true })
            setProcessingAuth(false)
            return
          }
        }

        if (code && !auth && invalidAuthCode) {
          setProcessingAuth(false)
          return
        }
      } catch (error) {
        console.error("[Auth] StudentRoleAuth error:", error)
        setProcessingAuth(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  return null
}

const PlatformOverrideAuth = ({
  platform,
  setProcessingAuth,
}: {
  platform: Platforms
  setProcessingAuth: (value: boolean) => void
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const code = searchParams.get("code")

  useEffect(() => {
    const run = async () => {
      searchParams.set(importantSearchParams.platformOverride, platform)
      setSearchParams(searchParams, { replace: true })

      console.log(`[Auth] PlatformOverrideAuth for platform: ${platform}`)
      try {
        const auth = AuthManager.getPlatformAuth(platform)

        // If "code" exists → process auth code
        if (code) {
          const newAuth = await AuthManager.processAuthCode(code, platform)
          if (newAuth) {
            searchParams.delete("code")
            searchParams.delete("platform")
            // Shadow is student only. Strip the param so it can't leak into
            // isShadow() through the URL check.
            searchParams.delete(SHADOW_SEARCH_PARAM)
            setSearchParams(searchParams, { replace: true })
            setProcessingAuth(false)
            return
          }
        }

        if (auth) {
          console.log(
            "[Auth] Found existing auth for platform override:",
            platform
          )
          const verified = await AuthManager.verifyToken(platform)
          if (verified) {
            setProcessingAuth(false)
            return
          }
          if (!verified) {
            AuthManager.clearAuthInfo(platform)
            setProcessingAuth(false)
            return
          }
        }

        if (!auth) {
          setProcessingAuth(false)
        }
      } catch (error) {
        console.error("[Auth] PlatformOverrideAuth error:", error)
        setProcessingAuth(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  return null
}

const AuthManagerProvider = () => {
  const platform = detectPlatformFromPath()

  const auth = AuthManager.getPlatformAuth(platform)

  const isStudent = platform === Platforms.student

  const [searchParams] = useSearchParams()
  const code = searchParams.get("code")

  const [processingAuth, setProcessingAuth] = useState(
    code || auth ? true : false
  )

  const sharedProps = {
    platform,
    auth,
    setProcessingAuth,
  }

  return (
    <>
      {isStudent ? (
        <StudentRoleAuth {...sharedProps} />
      ) : (
        <PlatformOverrideAuth {...sharedProps} />
      )}
      {processingAuth ? (
        <FullPageLoading debugMessage="Processing Auth" />
      ) : (
        <Outlet />
      )}
    </>
  )
}

export default AuthManagerProvider
